import React, { useRef, useState, useCallback, useEffect } from 'react';
import { YearbookPage, YearbookElement, Position, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import DraggableElement from '../DraggableElement';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

export type MobileViewMode = 'single' | 'double';

interface MobilePageViewProps {
  pages: YearbookPage[];
  currentPageIndex: number;
  selectedElementIds: string[];
  selectedElements: YearbookElement[];
  loadedIndices: Set<number>;
  viewMode: MobileViewMode;
  onSelectPage: (index: number) => void;
  onSelectElement: (id: string | null, metaKey?: boolean) => void;
  onUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  onDragUpdateElement: (id: string, updates: Partial<YearbookElement>) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

/** Double-page spreads: covers (4th + 1st) together, page 1 alone, then 2-3, 4-5… */
export function getMobileSpreads(pages: YearbookPage[]): number[][] {
  const back = pages.findIndex((p) => p.coverType === 'back');
  const front = pages.findIndex((p) => p.coverType === 'front');
  const spreads: number[][] = [];
  const cover = [back, front].filter((i) => i >= 0);
  if (cover.length) spreads.push(cover);
  const inner = pages.map((_, i) => i).filter((i) => !pages[i].coverType);
  if (inner.length) spreads.push([inner[0]]);
  for (let i = 1; i < inner.length; i += 2) spreads.push(inner.slice(i, i + 2));
  return spreads;
}

export function findSpreadIndex(spreads: number[][], pages: YearbookPage[], index: number): number {
  let s = spreads.findIndex((sp) => sp.includes(index));
  if (s < 0 && pages[index]?.coverType) s = 0; // tranche → covers
  return Math.max(0, s);
}

const VERTICAL_PADDING = 16;
const HORIZONTAL_PADDING = 12;
const DOUBLE_GAP = 8;
const BG_BLEED = 14.1732;

const MobilePageView: React.FC<MobilePageViewProps> = ({
  pages,
  currentPageIndex,
  selectedElementIds,
  loadedIndices,
  viewMode,
  onSelectPage,
  onSelectElement,
  onUpdateElement,
  onDragUpdateElement,
  onDragStart,
  onDragEnd,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);

  const spreads = React.useMemo(() => getMobileSpreads(pages), [pages]);
  const spreadIdx = findSpreadIndex(spreads, pages, currentPageIndex);
  const visible: number[] =
    viewMode === 'double' ? (spreads[spreadIdx] ?? [currentPageIndex]) : [currentPageIndex];
  const visibleKey = visible.join(',');

  const computeScale = useCallback(() => {
    if (!containerRef.current) return;
    const c = containerRef.current;
    const availW = c.clientWidth - HORIZONTAL_PADDING * 2;
    const availH = c.clientHeight - VERTICAL_PADDING * 2;
    const idxs = visibleKey.split(',').map(Number);
    const pagesW = idxs.reduce((sum, i) => sum + (pages[i]?.pageWidth ?? PAGE_WIDTH), 0);
    const gaps = (idxs.length - 1) * DOUBLE_GAP;
    const s = Math.min((availW - gaps) / pagesW, availH / PAGE_HEIGHT);
    setScale(Math.max(0.05, s));
  }, [pages, visibleKey]);

  useEffect(() => {
    computeScale();
    const obs = new ResizeObserver(computeScale);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [computeScale]);

  const page = pages[currentPageIndex];

  // Swipe navigation
  const goPrev = () => {
    if (viewMode === 'double') { if (spreadIdx > 0) onSelectPage(spreads[spreadIdx - 1][0]); }
    else if (currentPageIndex > 0) onSelectPage(currentPageIndex - 1);
  };
  const goNext = () => {
    if (viewMode === 'double') { if (spreadIdx < spreads.length - 1) onSelectPage(spreads[spreadIdx + 1][0]); }
    else if (currentPageIndex < pages.length - 1) onSelectPage(currentPageIndex + 1);
  };
  const canPrev = viewMode === 'double' ? spreadIdx > 0 : currentPageIndex > 0;
  const canNext = viewMode === 'double' ? spreadIdx < spreads.length - 1 : currentPageIndex < pages.length - 1;
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    const dt = Date.now() - touchStart.current.t;
    touchStart.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 600) {
      if (dx < 0) goNext();
      else if (dx > 0) goPrev();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.canvas) {
      onSelectElement(null);
    }
  };

  if (!page) return null;

  const renderPageCanvas = (p: YearbookPage, index: number, interactive: boolean) => {
    const isLoaded = loadedIndices.has(index);
    const w = (p.pageWidth ?? PAGE_WIDTH) * scale;
    const h = (PAGE_HEIGHT) * scale;
    const bgIsImage = p.background.type === 'image';
    const hasUserBg =
      bgIsImage &&
      (p.background.trame_background_id != null || p.background.trame_background_custom_id != null);
    const bgInset = hasUserBg ? BG_BLEED : 0;

    return (
      <div
        key={p.id}
        className="relative editor-shadow bg-white overflow-hidden shrink-0"
        style={{ width: w, height: h }}
        data-canvas="true"
        onClick={interactive ? handleBackgroundClick : undefined}
      >
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/40">
            <Loader2 className="animate-spin text-muted-foreground" size={24} />
          </div>
        )}

        {isLoaded && bgIsImage && (
          <img
            src={p.background.value}
            alt=""
            className="absolute pointer-events-none"
            style={{
              top: -bgInset * scale,
              left: -bgInset * scale,
              width: ((p.pageWidth ?? PAGE_WIDTH) + bgInset * 2) * scale,
              height: ((PAGE_HEIGHT) + bgInset * 2) * scale,
              maxWidth: 'none',
              objectFit: 'cover',
            }}
            draggable={false}
          />
        )}

        {isLoaded && (
          <div
            className="origin-top-left absolute top-0 left-0"
            style={{
              width: p.pageWidth ?? PAGE_WIDTH,
              height: PAGE_HEIGHT,
              transform: `scale(${scale})`,
              backgroundColor: !bgIsImage ? p.background.value : undefined,
            }}
            data-canvas="true"
            onClick={interactive ? handleBackgroundClick : undefined}
          >
            <div className={interactive ? undefined : 'pointer-events-none'}>
              {p.elements.map((el, idx) => (
                <DraggableElement
                  key={el.id}
                  element={el}
                  mobileInteraction
                  isSelected={interactive && selectedElementIds.includes(el.id)}
                  isInMultiSelection={false}
                  onSelect={(id, _pi, metaKey) => onSelectElement(id, metaKey)}
                  onUpdate={onUpdateElement}
                  onDragUpdate={onDragUpdateElement}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  scale={scale}
                  allElements={p.elements}
                  elementIndex={idx + 1}
                  pageIndex={index}
                />
              ))}
            </div>
          </div>
        )}

        {!interactive && (
          <button
            type="button"
            aria-label={p.name?.trim() || `Page ${p.pageNumber}`}
            className="absolute inset-0 z-10 cursor-pointer bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              onSelectPage(index);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0 min-w-0 w-full bg-editor-canvas relative overflow-hidden flex items-center justify-center"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleBackgroundClick}
    >
      {/* Prev / next buttons */}
      <button
        disabled={!canPrev}
        onClick={(e) => { e.stopPropagation(); goPrev(); }}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-editor-sidebar/80 text-editor-sidebar-foreground active:bg-editor-sidebar disabled:opacity-20 transition-all"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        disabled={!canNext}
        onClick={(e) => { e.stopPropagation(); goNext(); }}
        className="absolute right-1 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-editor-sidebar/80 text-editor-sidebar-foreground active:bg-editor-sidebar disabled:opacity-20 transition-all"
      >
        <ChevronRight size={20} />
      </button>

      <div className="flex items-center justify-center" style={{ gap: DOUBLE_GAP }}>
        {visible.map((i) => renderPageCanvas(pages[i], i, i === currentPageIndex))}
      </div>
    </div>
  );
};

export default MobilePageView;